import { Button } from "@/components/ui/baseUi/button"
import { Label } from "@/components/ui/baseUi/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/baseUi/radio-group"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/baseUi/select"
import { Text } from "@/components/ui/baseUi/text"
import { DropdownInput } from "@/components/ui/forms/dropdownInput"
import { withForm } from "@/components/ui/forms/hooks/useFormHook"
import { cn } from "@/modules/lib/utils"
import { BiweeklyPeriod, PayoutFrequencyType, WeeklyAnchorStripe } from "@modules/box/boxPayoutFrequencyTypes"
import { type BoxSettingsFormFields } from "@modules/box/boxSettingsFormikType"
import { word } from "@modules/core/utils/i18n"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { HUBanner } from "@modules/ui/components/huBanner"
import { useMemo } from "react"

export const BoxPayoutFrequencySelector = withForm({
  defaultValues: {} as BoxSettingsFormFields,
  render: function BoxPayoutFrequencySelectorInner({ form }) {
    const language = useLanguage()

    const biweeklyPeriodOptions = useMemo(
      () => [
        {
          label: word("box.form.payoutFrequency.biweeklyAnchor.firstAndFifteenth"),
          value: BiweeklyPeriod.FIRST_AND_THIRD_WEEK,
        },
        {
          label: word("box.form.payoutFrequency.biweeklyAnchor.eighthAndTwentySecond"),
          value: BiweeklyPeriod.SECOND_AND_FOURTH_WEEK,
        },
      ],
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [language],
    )

    const weekdayOptions = useMemo(
      () => [
        { label: word("global.day.MONDAY"), value: WeeklyAnchorStripe.MONDAY },
        { label: word("global.day.TUESDAY"), value: WeeklyAnchorStripe.TUESDAY },
        { label: word("global.day.WEDNESDAY"), value: WeeklyAnchorStripe.WEDNESDAY },
        { label: word("global.day.THURSDAY"), value: WeeklyAnchorStripe.THURSDAY },
        { label: word("global.day.FRIDAY"), value: WeeklyAnchorStripe.FRIDAY },
      ],
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [language],
    )

    const calendarDays = Array.from({ length: 31 }, (_, i) => i + 1)

    return (
      <form.AppField name="payoutFrequency">
        {(field) => {
          const payoutFrequency = field.state.value
          const frequencyType = payoutFrequency.type

          const handleFrequencyTypeChange = (type: PayoutFrequencyType) => {
            field.handleChange({
              type,
              dayCalendar: type === PayoutFrequencyType.MONTHLY ? (payoutFrequency.dayCalendar ?? 5) : undefined,
              weeklyAnchorStripe:
                type === PayoutFrequencyType.WEEKLY
                  ? (payoutFrequency.weeklyAnchorStripe ?? WeeklyAnchorStripe.MONDAY)
                  : undefined,
              biweeklyPeriod:
                type === PayoutFrequencyType.BIWEEKLY
                  ? (payoutFrequency.biweeklyPeriod ?? BiweeklyPeriod.FIRST_AND_THIRD_WEEK)
                  : undefined,
            })
          }

          const handleDaySelect = (day: number) => {
            field.handleChange({ ...payoutFrequency, dayCalendar: day })
          }

          return (
            <div className="my-4 flex flex-col gap-4">
              <div className="flex flex-col">
                <Text fontStyle="TM">{word("box.form.payoutFrequency.title")}</Text>
                <Text fontStyle="BM" className="text-textDefault-caption">
                  {word("box.form.payoutFrequency.description")}
                </Text>
              </div>

              <HUBanner type="warning" text={word("box.form.payoutFrequency.warning")} />

              <div className="flex flex-col gap-3">
                <RadioGroup
                  value={frequencyType}
                  onValueChange={(value) => {
                    handleFrequencyTypeChange(value as PayoutFrequencyType)
                  }}
                >
                  {/* MONTHLY */}
                  <div className="flex w-full flex-row justify-between rounded-2xl bg-neutral-200 p-4">
                    <div className="mt-4 flex flex-col items-start">
                      <div className="flex items-center gap-3">
                        <RadioGroupItem id="monthly" value={PayoutFrequencyType.MONTHLY} />
                        <Label htmlFor="monthly">{word("box.form.payoutFrequency.type.monthly")}</Label>
                      </div>
                    </div>

                    <div className="flex w-96 justify-end">
                      <div
                        className="grid gap-2"
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(7, 1fr)",
                        }}
                      >
                        {calendarDays.map((day) => {
                          const isSelected = payoutFrequency.dayCalendar === day
                          const enabled = frequencyType === PayoutFrequencyType.MONTHLY
                          const color = enabled ? "var(--textPrimary)" : "var(--textDefault-caption)"

                          return (
                            <Button
                              disabled={!enabled}
                              key={day}
                              variant="ghost"
                              size="iconMd"
                              onClick={() => {
                                handleDaySelect(day)
                              }}
                              className={cn(
                                "flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border-none",
                                isSelected && enabled ? "bg-primary-400" : "bg-transparent",
                              )}
                            >
                              <Text fontStyle="TS" color={color}>
                                {day}
                              </Text>
                            </Button>
                          )
                        })}
                      </div>
                    </div>
                  </div>

                  {/* BIWEEKLY */}
                  <div className="rounded-2xl bg-neutral-200 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex gap-3">
                        <RadioGroupItem id="biweekly" value={PayoutFrequencyType.BIWEEKLY} />
                        <Label htmlFor="biweekly">{word("box.form.payoutFrequency.type.biweekly")}</Label>
                      </div>

                      <DropdownInput
                        options={biweeklyPeriodOptions}
                        isRequiredInput={true}
                        className="w-96 rounded-4xl border border-neutral-400 bg-neutral-100"
                      >
                        <Select
                          defaultValue={payoutFrequency.biweeklyPeriod ?? BiweeklyPeriod.FIRST_AND_THIRD_WEEK}
                          onValueChange={(value) => {
                            field.handleChange({
                              ...payoutFrequency,
                              biweeklyPeriod: value ?? undefined,
                            })
                          }}
                          items={biweeklyPeriodOptions}
                          disabled={frequencyType !== PayoutFrequencyType.BIWEEKLY}
                        >
                          <SelectTrigger id={"durationType"}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {biweeklyPeriodOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </DropdownInput>
                    </div>
                  </div>

                  {/* WEEKLY */}
                  <div className="rounded-2xl bg-neutral-200 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex gap-3">
                        <RadioGroupItem id="weekly" value={PayoutFrequencyType.WEEKLY} />
                        <Label htmlFor="weekly">{word("box.form.payoutFrequency.type.weekly")}</Label>
                      </div>

                      <DropdownInput
                        options={weekdayOptions}
                        isRequiredInput={true}
                        className="w-96 rounded-4xl border border-neutral-400 bg-neutral-100"
                      >
                        <Select
                          defaultValue={payoutFrequency.weeklyAnchorStripe ?? WeeklyAnchorStripe.MONDAY}
                          onValueChange={(value) => {
                            field.handleChange({
                              ...payoutFrequency,
                              weeklyAnchorStripe: value ?? undefined,
                            })
                          }}
                          items={weekdayOptions}
                          disabled={frequencyType !== PayoutFrequencyType.WEEKLY}
                        >
                          <SelectTrigger id={"durationType"}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {weekdayOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </DropdownInput>
                    </div>
                  </div>
                </RadioGroup>
              </div>
            </div>
          )
        }}
      </form.AppField>
    )
  },
})
